import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Heart, X, Loader2, Users, Phone, Send, Globe2, MapPin } from "lucide-react";
import { useTranslation } from "@/i18n/LanguageContext";
import LangSwitcher from "@/components/LangSwitcher";
import VMark from "@/components/vangard/VMark";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREMIUM = new Set(["gold", "vip", "corporate"]);

export default function MatchingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("deck");
  const [candidates, setCandidates] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(null); // "tier" | "approval" | null
  const [matchModal, setMatchModal] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: user } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setMe(user);
        const tier = (user.membership_tier || "silver").toLowerCase();
        if (!PREMIUM.has(tier)) { setLocked("tier"); return; }
        if (user.application_status !== "approved") { setLocked("approval"); return; }
        await refresh();
      } catch {
        navigate("/login");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    const [c, m] = await Promise.all([
      axios.get(`${API}/matching/candidates`, { withCredentials: true }),
      axios.get(`${API}/matching/matches`, { withCredentials: true }),
    ]);
    setCandidates(c.data.candidates || []);
    setMatches(m.data.matches || []);
  };

  const swipe = async (target, decision) => {
    setCandidates((prev) => prev.filter((c) => c.user_id !== target.user_id));
    try {
      const { data } = await axios.post(
        `${API}/matching/swipe`,
        { target_user_id: target.user_id, decision },
        { withCredentials: true },
      );
      if (data.match) {
        setMatchModal(data.with);
        setMatches((prev) => [data.with, ...prev]);
      }
    } catch (e) {
      toast.error(t("vc.matching.swipe-fail"));
    }
  };

  if (loading) return <FullscreenLoader />;
  if (locked) return <LockedMatching t={t} navigate={navigate} tier={(me?.membership_tier || "silver").toLowerCase()} reason={locked} />;

  return (
    <div className="min-h-screen bg-[#050505] text-white noise-overlay" data-testid="matching-page">
      <Seo title={t("vc.acct.nav.matching")} noindex />
      <header className="fixed top-0 left-0 right-0 z-40 backdrop-blur-xl bg-[#050505]/75 border-b border-white/[0.06]">
        <div className="max-w-[1100px] mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/account/profile")} className="flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-neutral-400 hover:text-white transition-colors" data-testid="matching-back">
            <ArrowLeft size={16} /> {t("vc.back")}
          </button>
          <div className="font-serif text-lg">Vangard<span className="text-neutral-400">Club</span></div>
          <LangSwitcher />
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-6 pt-32 pb-24">
        <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-400">{t("vc.matching.eyebrow")}</div>
        <h1 className="font-serif text-5xl lg:text-6xl mt-4 leading-[1.05]">
          {t("vc.matching.title-1")} <span className="italic text-neutral-300">{t("vc.matching.italic")}</span>
        </h1>
        <p className="mt-6 text-base text-neutral-400 font-light max-w-2xl">{t("vc.matching.sub")}</p>

        {/* Tabs */}
        <div className="mt-10 inline-flex rounded-full border border-white/[0.10] p-1">
          {[
            { id: "deck", label: t("vc.matching.tab-deck") },
            { id: "matches", label: `${t("vc.matching.tab-matches")} · ${matches.length}` },
          ].map((tt) => (
            <button
              key={tt.id}
              onClick={() => setTab(tt.id)}
              data-testid={`matching-tab-${tt.id}`}
              className={`px-5 py-2 rounded-full text-[11px] tracking-[0.25em] uppercase transition-colors ${tab === tt.id ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`}
            >
              {tt.label}
            </button>
          ))}
        </div>

        {tab === "deck" ? (
          <Deck candidates={candidates} onSwipe={swipe} t={t} onRefresh={refresh} />
        ) : (
          <MatchesList matches={matches} t={t} />
        )}
      </main>

      <AnimatePresence>{matchModal && <MatchModal card={matchModal} onClose={() => setMatchModal(null)} t={t} />}</AnimatePresence>
    </div>
  );
}

function FullscreenLoader() {
  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
      <Loader2 className="animate-spin" size={22} />
    </div>
  );
}

function LockedMatching({ t, navigate, tier, reason }) {
  const isApproval = reason === "approval";
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid={isApproval ? "matching-pending" : "matching-locked"}>
      <div className="max-w-[900px] mx-auto px-6 py-32 text-center">
        <VMark position="center" size={900} opacity={0.05} />
        <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-400">
          {isApproval ? t("vc.onb.status.submitted") : t("vc.matching.locked-eyebrow")}
        </div>
        <h1 className="font-serif text-5xl sm:text-6xl mt-4 leading-[1.05]">
          {isApproval ? (
            <>{t("vc.lock.title-1")} <span className="italic text-neutral-300">{t("vc.lock.italic")}</span></>
          ) : (
            <>{t("vc.matching.locked-title-1")} <span className="italic text-neutral-300">{t("vc.matching.locked-italic")}</span></>
          )}
        </h1>
        <p className="mt-6 text-neutral-400 font-light leading-relaxed max-w-[52ch] mx-auto">
          {isApproval ? t("vc.lock.body") : t("vc.matching.locked-body")}
        </p>
        {!isApproval && (
          <div className="mt-4 text-[11px] tracking-[0.25em] uppercase text-neutral-500">
            {t("vc.matching.locked-tier")}: {tier.toUpperCase()}
          </div>
        )}
        <div className="mt-10 flex gap-3 justify-center">
          {isApproval ? (
            <button onClick={() => navigate("/onboarding")} className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors" data-testid="matching-open-onboarding">
              {t("vc.lock.cta-onb")}
            </button>
          ) : (
            <button onClick={() => navigate("/account/membership")} className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors" data-testid="matching-upgrade">
              {t("vc.matching.upgrade")}
            </button>
          )}
          <button onClick={() => navigate("/account/profile")} className="inline-flex items-center gap-3 border border-white/25 text-white px-8 py-4 rounded-full text-[12px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/55 transition-colors">
            {t("vc.back")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Deck({ candidates, onSwipe, t, onRefresh }) {
  if (!candidates.length) {
    return (
      <div className="mt-16 rounded-3xl border border-white/[0.08] bg-[#0A0A0A] p-10 text-center" data-testid="matching-empty">
        <Users size={28} className="mx-auto text-neutral-500" />
        <h3 className="font-serif text-2xl mt-6">{t("vc.matching.empty-title")}</h3>
        <p className="mt-3 text-sm text-neutral-400 font-light">{t("vc.matching.empty-body")}</p>
        <button onClick={onRefresh} className="mt-6 inline-flex items-center gap-2 border border-white/25 text-white px-6 py-3 rounded-full text-[11px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/55 transition-colors" data-testid="matching-refresh">
          {t("vc.matching.refresh")}
        </button>
      </div>
    );
  }
  const top = candidates[0];
  const next = candidates[1];
  return (
    <div className="mt-10 relative h-[560px] max-w-md mx-auto">
      {next && <MatchCard key={next.user_id} card={next} stackIndex={1} />}
      <MatchCard key={top.user_id} card={top} stackIndex={0} onSwipe={(d) => onSwipe(top, d)} t={t} interactive />
      <div className="absolute -bottom-6 left-0 right-0 flex justify-center gap-6 z-10">
        <SwipeBtn onClick={() => onSwipe(top, "pass")} testid="swipe-pass" ariaLabel={t("vc.matching.pass")}>
          <X size={22} />
        </SwipeBtn>
        <SwipeBtn onClick={() => onSwipe(top, "interested")} filled testid="swipe-interested" ariaLabel={t("vc.matching.interested")}>
          <Heart size={20} />
        </SwipeBtn>
      </div>
    </div>
  );
}

function SwipeBtn({ children, onClick, filled, testid, ariaLabel }) {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      data-testid={testid}
      className={`w-14 h-14 rounded-full flex items-center justify-center transition-all shadow-lg ${filled ? "bg-white text-black hover:scale-105" : "bg-black border border-white/25 text-white hover:border-white/60"}`}
    >
      {children}
    </button>
  );
}

function MatchCard({ card, stackIndex, onSwipe, t, interactive }) {
  const x = useMotionValue(0);
  const rot = useTransform(x, [-260, 260], [-18, 18]);
  const opacityPass = useTransform(x, [-160, -40], [1, 0]);
  const opacityLike = useTransform(x, [40, 160], [0, 1]);

  const handleEnd = (_, info) => {
    if (!interactive) return;
    if (info.offset.x > 140) onSwipe("interested");
    else if (info.offset.x < -140) onSwipe("pass");
  };

  const scale = 1 - stackIndex * 0.04;
  const translateY = stackIndex * 12;

  return (
    <motion.div
      className="absolute inset-0 rounded-3xl overflow-hidden border border-white/[0.10] bg-[#0E0E0E] shadow-2xl"
      style={interactive ? { x, rotate: rot } : { scale, y: translateY }}
      drag={interactive ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={handleEnd}
      whileTap={interactive ? { cursor: "grabbing" } : {}}
      data-testid={interactive ? `swipe-card-${card.user_id}` : undefined}
    >
      {/* Portrait */}
      <div className="relative h-[280px] bg-neutral-900 overflow-hidden">
        {card.picture ? (
          <img src={card.picture} alt={card.name} className="w-full h-full object-cover grayscale contrast-[1.05]" />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-serif text-6xl text-neutral-700">
            {(card.name || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
        {interactive && (
          <>
            <motion.div style={{ opacity: opacityLike }} className="absolute top-6 right-6 rotate-[6deg] border-2 border-white text-white px-4 py-1 rounded-full text-[11px] tracking-[0.35em] uppercase">
              {t("vc.matching.stamp-yes")}
            </motion.div>
            <motion.div style={{ opacity: opacityPass }} className="absolute top-6 left-6 -rotate-[6deg] border-2 border-white/60 text-white/80 px-4 py-1 rounded-full text-[11px] tracking-[0.35em] uppercase">
              {t("vc.matching.stamp-pass")}
            </motion.div>
          </>
        )}
        <div className="absolute bottom-4 left-6 right-6">
          <div className="font-serif text-3xl">{card.name}</div>
          {card.headline && <div className="text-sm text-neutral-300 font-light mt-1">{card.headline}</div>}
        </div>
      </div>

      <div className="p-6 space-y-4 text-sm">
        {(card.role || card.company) && (
          <div className="text-neutral-300 font-light">
            {card.role}{card.role && card.company ? " · " : ""}{card.company}
          </div>
        )}
        {(card.industry || card.location) && (
          <div className="flex flex-wrap gap-3 text-[11px] tracking-[0.15em] uppercase text-neutral-500">
            {card.industry && <span>{card.industry}</span>}
            {card.location && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {card.location}</span>}
          </div>
        )}
        {card.expertise?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {card.expertise.slice(0, 6).map((e) => (
              <span key={e} className="text-[11px] font-light border border-white/[0.12] rounded-full px-2.5 py-1 text-neutral-300">{e}</span>
            ))}
          </div>
        )}
        {card.offering && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{t("vc.account.offering")}</div>
            <div className="mt-1 text-neutral-300 font-light leading-relaxed">{card.offering}</div>
          </div>
        )}
        {card.looking_for && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">{t("vc.account.looking-for")}</div>
            <div className="mt-1 text-neutral-300 font-light leading-relaxed">{card.looking_for}</div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function MatchesList({ matches, t }) {
  if (!matches.length) {
    return (
      <div className="mt-16 rounded-3xl border border-white/[0.08] bg-[#0A0A0A] p-10 text-center" data-testid="matches-empty">
        <Users size={28} className="mx-auto text-neutral-500" />
        <h3 className="font-serif text-2xl mt-6">{t("vc.matching.matches-empty-title")}</h3>
        <p className="mt-3 text-sm text-neutral-400 font-light">{t("vc.matching.matches-empty-body")}</p>
      </div>
    );
  }
  return (
    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="matches-list">
      {matches.map((m) => <MatchRow key={m.user_id} m={m} t={t} />)}
    </div>
  );
}

function MatchRow({ m, t }) {
  return (
    <div className="rounded-3xl border border-white/[0.08] bg-[#0A0A0A] p-5 flex gap-4">
      <div className="w-16 h-16 rounded-2xl overflow-hidden bg-neutral-900 shrink-0">
        {m.picture ? (
          <img src={m.picture} alt="" className="w-full h-full object-cover grayscale" />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-serif text-2xl text-neutral-600">
            {(m.name || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-serif text-lg">{m.name}</div>
        {m.headline && <div className="text-xs text-neutral-400 font-light">{m.headline}</div>}
        <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-neutral-300">
          {m.telegram_id && (
            <a href={`https://t.me/${m.telegram_id.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-white transition-colors" data-testid={`match-tg-${m.user_id}`}>
              <Send size={12} /> {m.telegram_id}
            </a>
          )}
          {m.phone && (
            <a href={`tel:${m.phone}`} className="inline-flex items-center gap-1.5 hover:text-white transition-colors" data-testid={`match-phone-${m.user_id}`}>
              <Phone size={12} /> {m.phone}
            </a>
          )}
          {m.website && (
            <a href={m.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-white transition-colors">
              <Globe2 size={12} /> {t("vc.matching.website")}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function MatchModal({ card, onClose, t }) {
  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="match-modal"
    >
      <motion.div
        className="max-w-md w-full rounded-3xl border border-white/[0.12] bg-[#0A0A0A] p-10 text-center"
        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-400">{t("vc.matching.match-eyebrow")}</div>
        <h2 className="font-serif text-4xl mt-4 leading-tight">
          {t("vc.matching.match-title-1")} <span className="italic text-neutral-300">{t("vc.matching.match-italic")}</span>
        </h2>
        <div className="mt-8 w-28 h-28 mx-auto rounded-full overflow-hidden bg-neutral-900 border border-white/10">
          {card.picture ? (
            <img src={card.picture} alt="" className="w-full h-full object-cover grayscale" />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-serif text-4xl text-neutral-600">
              {(card.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>
        <div className="mt-6 font-serif text-2xl">{card.name}</div>
        {card.headline && <div className="text-sm text-neutral-400 font-light mt-1">{card.headline}</div>}
        <div className="mt-6 flex flex-col gap-2 items-center">
          {card.telegram_id && (
            <a href={`https://t.me/${card.telegram_id.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-white transition-colors">
              <Send size={14} /> {card.telegram_id}
            </a>
          )}
          {card.phone && (
            <a href={`tel:${card.phone}`} className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-white transition-colors">
              <Phone size={14} /> {card.phone}
            </a>
          )}
        </div>
        <button onClick={onClose} className="mt-8 inline-flex items-center gap-3 bg-white text-black px-8 py-3.5 rounded-full text-[11px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors" data-testid="match-modal-close">
          {t("vc.matching.match-done")}
        </button>
      </motion.div>
    </motion.div>
  );
}
